import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useHistory, useRouteMatch } from 'react-router-dom';
import { NotificationManager } from 'react-notifications';

import {
  validateSubmissionSource,
  INVALID_SUB_SOURCE,
  SYB_SUB_SOURCE,
  PARTNER_SUB_SOURCE,
} from '../../catalog/utils';
import {
  createCycleTiming,
  searchForCycleTiming,
  upsertItemsToCycleTiming,
  updateCycleTiming,
  getCycleTimingStations,
  getCycleTimingById,
} from '../../../services/cycleTimings/services';
import {
  SearchOrCreateCycleTiming,
  CycleTiming,
  Status,
} from '../../../services/cycleTimings/types';

const handleTimingServiceErrors = (err: any) => {
  console.error(err);
  if (err?.response?.data?.message) {
    NotificationManager.error(
      err?.response?.data?.message,
      err?.response?.data?.name || 'Error',
      3000,
    );
  } else {
    NotificationManager.error('Unknown error please contact IT.', 'Error', 3000);
  }
};

const searchForExistingTiming = async (searchOrCreateTiming: SearchOrCreateCycleTiming) => {
  let existingTiming: CycleTiming | undefined;

  try {
    const existingTimingRes = await searchForCycleTiming(searchOrCreateTiming);
    if (existingTimingRes) {
      existingTiming = existingTimingRes.data;
    }
  } catch (err) {
    // only log errors if not found error
    if (err?.response?.data?.name !== 'NotFoundError') {
      handleTimingServiceErrors(err);
    }
  } finally {
    return existingTiming;
  }
};

/**
 *  Notes:
 * - submissions must start with an "S" or "P" so we know where the submission lives
 *   - "S" - SYB submission - DB: tup.submissions
 *   - "P" - Partner submission - DB: bd.submissions
 */
const checkAndSetupValidItem = ({
  submissionId,
  sku,
  station,
}: {
  submissionId?: string;
  sku?: string;
  station: string;
}) => {
  const searchOrCreateTimingItem: SearchOrCreateCycleTiming = { station };
  let isInvalidSubmission: true | undefined;

  // Setup search/payload - parse submission or sku
  if (submissionId) {
    const [source, validSubmissionID] = validateSubmissionSource(submissionId);
    if (source === INVALID_SUB_SOURCE) {
      isInvalidSubmission = true;
    } else {
      if (source === SYB_SUB_SOURCE) {
        searchOrCreateTimingItem.sybSubmissionId = validSubmissionID;
      } else if (source === PARTNER_SUB_SOURCE) {
        searchOrCreateTimingItem.partnerSubmissionId = validSubmissionID;
      }
    }
  } else if (sku) {
    searchOrCreateTimingItem.sku = sku;
  }
  return { isInvalidSubmission, searchOrCreateTimingItem };
};

type SetStateString = React.Dispatch<React.SetStateAction<string>>;
const setupTimingTabs = ({
  sku,
  setSku,
  submissionId,
  setSubmissionId,
}: {
  sku: string;
  setSku: SetStateString;
  submissionId: string;
  setSubmissionId: SetStateString;
}) => [
  {
    tabLabel: 'SKU',
    id: 'skuSearchBox',
    value: sku,
    variant: 'outlined',
    placeholder: 'Search by SKU',
    onChange: (e: any) => setSku(e.target.value),
  },
  {
    tabLabel: 'Submission ID',
    id: 'submissionSearchBox',
    value: submissionId,
    variant: 'outlined',
    placeholder: 'Submission e.g. s12345, p1234',
    onChange: (e: any) => setSubmissionId(e.target.value),
  },
];

type SearchedInput = {
  sku?: string;
  submissionId?: string;
};
type CycleTimingsRouteState = { station?: string };
const SEARCHED_SKU = 'searchedSku';
const SEARCHED_SUBMISSION = 'searchedSubmissionId';

export const useCycleTimings = () => {
  const matchCycleTimingId = useRouteMatch<{ cycleTimingId?: string }>({
    path: '/service/cycle-timings/:cycleTimingId',
    strict: true,
  });
  const cycleTimingIdUrlParam = matchCycleTimingId?.params.cycleTimingId;

  const previousCycleTimingIdUrlParamRef = useRef<string | null>();
  const previousCycleTimingIdUrlParam = previousCycleTimingIdUrlParamRef.current;
  useEffect(() => {
    previousCycleTimingIdUrlParamRef.current = cycleTimingIdUrlParam;
  });

  const history = useHistory<CycleTimingsRouteState>();
  const location = useLocation<CycleTimingsRouteState>();
  const currentStation = location.state?.station;

  const searchParams = new URLSearchParams(location.search);
  const searchedSku = searchParams.get(SEARCHED_SKU);
  const searchedSubmissionId = searchParams.get(SEARCHED_SUBMISSION);

  const [station, setStation] = useState<string>(currentStation || '');
  const [stations, setStations] = useState<{ id: string; description: string }[] | undefined>();
  const [submissionId, setSubmissionId] = useState<string>(searchedSubmissionId || '');
  const [sku, setSku] = useState<string>(searchedSku || '');

  const initialTab = !!searchedSku ? 0 : !!searchedSubmissionId ? 1 : 0;
  const [tabValue, setTabValue] = useState(initialTab);

  const [loading, setLoading] = useState(false);
  const [cycleTiming, setCycleTiming] = useState<CycleTiming | undefined>();
  const [newTimingForExisting, setNewTimingForExisting] = useState<
    SearchOrCreateCycleTiming | undefined
  >();
  const [addItemToTimingForExisting, setAddItemToTimingForExisting] = useState<
    SearchOrCreateCycleTiming | undefined
  >();

  const clearSearchValues = () => {
    setSubmissionId('');
    setSku('');
  };

  const clearCurrentTiming = useCallback(() => {
    setCycleTiming(undefined);
    history.push('/service/cycle-timings', { station });
  }, [history, station]);

  const updateCurrentCycleTiming = useCallback(
    ({
      updatedCycleTiming,
      updateHistory,
      searchedInput,
    }: {
      updatedCycleTiming: CycleTiming;
      updateHistory?: boolean;
      searchedInput?: SearchedInput;
    }) => {
      if (updateHistory) {
        let searchedInputParams: URLSearchParams | undefined;
        // Pass along searched sku or submission.
        // Used to pre-populate input if user wants to start new timing
        if (searchedInput) {
          searchedInputParams = new URLSearchParams();
          searchedInput.sku && searchedInputParams.set(SEARCHED_SKU, searchedInput.sku);
          searchedInput.submissionId &&
            searchedInputParams.set(SEARCHED_SUBMISSION, searchedInput.submissionId);
        }
        history.push(
          `/service/cycle-timings/${updatedCycleTiming.id}${
            searchedInputParams ? `?${searchedInputParams.toString()}` : ''
          }`,
        );
      } else {
        setCycleTiming(updatedCycleTiming);
      }
    },
    [history],
  );

  const currentCycleTimeId = cycleTiming?.id;
  const tryToLoadExistingTiming = useCallback(
    async (cycleTimingId: string) => {
      setLoading(true);
      try {
        const existingTimingRes = await getCycleTimingById(cycleTimingId);
        if (existingTimingRes && currentCycleTimeId !== existingTimingRes.data.id) {
          updateCurrentCycleTiming({ updatedCycleTiming: existingTimingRes.data });
          setStation(existingTimingRes.data.stationId);
        }
      } catch (err) {
        handleTimingServiceErrors(err);
      } finally {
        setLoading(false);
      }
    },
    [updateCurrentCycleTiming, currentCycleTimeId],
  );

  /**
   * Setup auto pause functionality:
   * - on tab/browser close
   * - on page route change
   */
  const currentCycleTimingStatus = cycleTiming?.status;
  useEffect(() => {
    const attemptToPause = async () => {
      try {
        if (currentCycleTimeId && currentCycleTimingStatus === Status.STARTED) {
          const successfulUpdate = await updateCycleTiming(currentCycleTimeId, {
            status: Status.PAUSED,
          });
          if (successfulUpdate && successfulUpdate.data.status === Status.PAUSED) {
            NotificationManager.warning(
              `Timing: ${currentCycleTimeId}`,
              'We paused your timer',
              2000,
            );
          }
        }
      } catch (err) {}
    };

    const removeHistoryListen = history.listen(attemptToPause);
    window.addEventListener('beforeunload', attemptToPause);

    return () => {
      window.removeEventListener('beforeunload', attemptToPause);
      /**
       * NOTE: This is wrapped in a timeout to put it on the end of the queue
       * This is because on browser back/forward the component is un-mounting
       * before the callback from the history listener is called.
       * - https://github.com/ReactTraining/history/issues/710#issuecomment-503407519
       */
      setTimeout(() => {
        removeHistoryListen();
      }, 0);
    };
  }, [currentCycleTimeId, currentCycleTimingStatus, history]);

  // Retrieve existing cycle timing by id, also handle route change with ids
  useEffect(() => {
    if (!!cycleTimingIdUrlParam && cycleTimingIdUrlParam !== previousCycleTimingIdUrlParam) {
      tryToLoadExistingTiming(cycleTimingIdUrlParam);
    }
  }, [cycleTimingIdUrlParam, previousCycleTimingIdUrlParam, tryToLoadExistingTiming]);

  // Retrieve all stations
  useEffect(() => {
    const setupStations = async () => {
      try {
        const stationsRes = await getCycleTimingStations();
        if (stationsRes) {
          setStations(stationsRes.data);
        }
      } catch (err) {
        handleTimingServiceErrors(err);
      }
    };
    setupStations();
  }, []);

  const timingTabs = setupTimingTabs({ sku, setSku, submissionId, setSubmissionId });
  const onTabChange = (_: React.ChangeEvent<{}>, newTab: any) => {
    if (newTab !== tabValue) {
      // Clear values on tab change
      clearSearchValues();
      setTabValue(newTab);
    }
  };

  const addItemToCycleTiming = async (
    searchOrCreateTimingItem: SearchOrCreateCycleTiming,
    forceNewForExistingCompletedTiming?: boolean,
  ) => {
    if (!cycleTiming) return;
    setLoading(true);
    try {
      await upsertItemsToCycleTiming(
        cycleTiming.id,
        [...cycleTiming.items, searchOrCreateTimingItem],
        forceNewForExistingCompletedTiming,
      );
      const updatedTimingRes = await getCycleTimingById(cycleTiming.id);
      if (updatedTimingRes) {
        updateCurrentCycleTiming({ updatedCycleTiming: updatedTimingRes.data });
        NotificationManager.success('Item to timing', 'Added', 3000);
      }
    } catch (err) {
      handleTimingServiceErrors(err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Flows:
   * - Branch A - No currently selected/displayed timer ( start new timer )
   * - Branch B - Current timer selected/displayed ( add to paused or running timing )
   *   - NOTE: api allows us to add to completed timings, but we block that via the FE
   * - Branch C - Current timer completed and displayed ( start new timing )
   *
   * - A:1 - Check if a timing exists for the submitted submission/sku & station
   *   - If a timing exists show that existing timing info
   * - A:2 - If timing doesn't already exist start a new timing
   *
   * - B:1 - Check if a timing exists for the submitted submission/sku & station
   *   - B:1:A - If a timing exists and still running Notify user and skip adding
   *   - B:1:B - If a timing exists and completed ask user if they'd like to add
   * - B:2 - If timing does not exist add submission/sku to existing timing
   *
   * - C:1 - Check if a timing exists for the submitted submission/sku & station
   *   - If a timing exists we confirm that the user wants to make a new one
   */
  const onFormSubmitSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    const { isInvalidSubmission, searchOrCreateTimingItem } = checkAndSetupValidItem({
      submissionId,
      sku,
      station,
    });
    if (isInvalidSubmission) {
      NotificationManager.warning(
        'ID must a number and being with "S" or "P"',
        `Invalid submission ID`,
        2500,
      );
      return;
    }

    try {
      // Branch A - No currently selected/displayed timer ( start new timer )
      if (!cycleTiming) {
        setLoading(true);
        const existingTiming = await searchForExistingTiming(searchOrCreateTimingItem);
        if (existingTiming && existingTiming.status !== Status.COMPLETED) {
          setLoading(false);
          // Pass along searched sku or submission.
          // Used to pre-populate input if user wants to start new timing
          updateCurrentCycleTiming({
            updatedCycleTiming: existingTiming,
            updateHistory: true,
            searchedInput: { sku, submissionId },
          });
        } else {
          const newTimingRes = await createCycleTiming({
            ...searchOrCreateTimingItem,
            forceNewForExistingCompletedTiming: true,
          });
          setLoading(false);
          if (newTimingRes) {
            NotificationManager.success('Timing created', 'New', 3000);
            updateCurrentCycleTiming({
              updatedCycleTiming: newTimingRes.data,
              updateHistory: true,
            });
          }
        }
      } else if (!!cycleTiming && cycleTiming.status !== Status.COMPLETED) {
        // Branch B - Current timer selected/displayed ( add to paused or running timing )
        setLoading(true);
        const existingTiming = await searchForExistingTiming(searchOrCreateTimingItem);
        if (existingTiming && existingTiming.status !== Status.COMPLETED) {
          // B:1
          setLoading(false);
          NotificationManager.error(
            'Existing uncompleted timing for item, unable to add',
            'Found',
            3000,
          );
        } else {
          // B:2
          await addItemToCycleTiming(searchOrCreateTimingItem, true);
        }
      } else if (!!cycleTiming && cycleTiming.status === Status.COMPLETED) {
        // Branch C - Current timer completed and displayed ( start new timing )
        await addItemToCycleTiming(searchOrCreateTimingItem, true);
      }
    } catch (err) {
      setLoading(false);
      handleTimingServiceErrors(err);
    }
  };

  const newTimingForExistingDialogOnCancel = () => {
    setNewTimingForExisting(undefined);
  };

  // User wants to start a new timing for an existing timing
  const newTimingForExistingDialogOnSubmit = async () => {
    if (loading || !newTimingForExisting) return;
    setLoading(true);
    try {
      const newTimingRes = await createCycleTiming({
        ...newTimingForExisting,
        forceNewForExistingCompletedTiming: true,
      });
      if (newTimingRes) {
        NotificationManager.success('Timing created', 'New', 3000);
        updateCurrentCycleTiming({
          updatedCycleTiming: newTimingRes.data,
          updateHistory: true,
        });
      }
    } catch (err) {
      handleTimingServiceErrors(err);
    } finally {
      setNewTimingForExisting(undefined);
      clearSearchValues();
      setLoading(false);
    }
  };

  const addItemToTimingForExistingDialogOnCancel = () => {
    setAddItemToTimingForExisting(undefined);
  };

  // User wants to add an existing completed timings item to a current timing
  const addItemToTimingForExistingDialogOnSubmit = async () => {
    if (!addItemToTimingForExisting) return;
    await addItemToCycleTiming(addItemToTimingForExisting, true);
    setAddItemToTimingForExisting(undefined);
  };

  const updateTimingStatus = async (status: Status) => {
    if (!cycleTiming?.id) return;
    setLoading(true);
    let successfullyCompletedAndReRouted = false;
    try {
      const updatedTimingRes = await updateCycleTiming(cycleTiming?.id, { status });
      if (updatedTimingRes) {
        // if we successfully completed a timing clear/reset the form
        if (updatedTimingRes.data.status === Status.COMPLETED) {
          NotificationManager.success('Successfully completed timing.', 'Completed', 2000);
          clearCurrentTiming();
          successfullyCompletedAndReRouted = true;
        } else {
          updateCurrentCycleTiming({
            updatedCycleTiming: updatedTimingRes.data,
          });
        }
      }
    } catch (err) {
      handleTimingServiceErrors(err);
    }
    if (!successfullyCompletedAndReRouted) {
      setLoading(false);
    }
  };

  const updateTimingTraining = async (isTraining: boolean) => {
    if (!cycleTiming?.id) return;
    setLoading(true);
    try {
      const updatedTimingRes = await updateCycleTiming(cycleTiming?.id, { isTraining });
      if (updatedTimingRes) {
        updateCurrentCycleTiming({
          updatedCycleTiming: updatedTimingRes.data,
        });
      }
    } catch (err) {
      handleTimingServiceErrors(err);
    } finally {
      setLoading(false);
    }
  };

  const removeTimingItem = async (id: number) => {
    // can only remove items when status is not completed
    if (!cycleTiming || cycleTiming.status === Status.COMPLETED) return;

    setLoading(true);

    // remove item by id from items
    const upsertItems = cycleTiming.items.filter(item => item.id !== id);
    if (upsertItems.length === 0) {
      NotificationManager.warning('Can not remove all items from a timing', 'Canceled', 3000);
    }

    try {
      await upsertItemsToCycleTiming(cycleTiming.id, upsertItems);

      const updatedTimingRes = await getCycleTimingById(cycleTiming.id);
      if (updatedTimingRes) {
        updateCurrentCycleTiming({ updatedCycleTiming: updatedTimingRes.data });
        NotificationManager.info('Item from timing', 'Removed', 3000);
      }
    } catch (err) {
      handleTimingServiceErrors(err);
    }
    setLoading(false);
  };

  const onSelectStation = (e: React.ChangeEvent<{ name?: string | undefined; value: unknown }>) => {
    if (typeof e.target.value === 'string') {
      setStation(e.target.value);
    }
  };

  const selectNewStation = () => {
    if (!cycleTiming) {
      setStation('');
    } else {
      history.push('/service/cycle-timings');
    }
  };

  return {
    station,
    setStation,
    stations,
    onSelectStation,
    loading,
    selectNewStation,
    onFormSubmitSearch,
    timingTabs,
    tabValue,
    onTabChange,
    cycleTiming,
    clearCurrentTiming,
    updateTimingStatus,
    removeTimingItem,
    newTimingForExistingDialogOpen: !!newTimingForExisting,
    newTimingForExistingDialogOnCancel,
    newTimingForExistingDialogOnSubmit,
    addItemToTimingForExistingDialogOpen: !!addItemToTimingForExisting,
    addItemToTimingForExistingDialogOnCancel,
    addItemToTimingForExistingDialogOnSubmit,
    updateTimingTraining,
  };
};
